/**
 * PublicHeader — the ONE header for every unauthenticated page.
 *
 * There used to be FOUR: `Top.js` (legal pages), `UniversalNavbar.js` (auth
 * pages), an inline `Nav()` in `pages/landing_page.js` and an inline `Header()`
 * in `pages/seo/SeoLandingPage.js`. Four logos at four sizes, four heights,
 * four link sets, four focus treatments — and one of them was still serving the
 * pre-rebrand wordmark from a hardcoded CDN path. Any brand change had to be
 * made in four places or the site contradicted itself, which is exactly what
 * happened.
 *
 * Two behaviours, one implementation:
 *
 *   variant="transparent"  clear over a hero, goes solid past 16px of scroll.
 *                          `fixed`, so the hero starts at the top of the
 *                          viewport. The landing page and nothing else.
 *   variant="solid"        opaque from the first pixel. `sticky`, so it takes
 *                          part in flow and the page below needs NO padding to
 *                          compensate — the ad-hoc `pt-32` / `mt-20` guesses
 *                          each legal page carried are gone.
 *
 * Different pages legitimately need different LINKS (a marketing nav vs an auth
 * page that should only offer a way back). That is the `links` / `secondary` /
 * `cta` props, not a second component.
 *
 * Logo comes from `utils/brandAssets` — the single source. Never hardcode a
 * brand URL here; one upload to static/brand/ must update every surface.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { INIKRU_LOGO as inikruLogo } from '../utils/brandAssets';

const FOCUS =
  'focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]';

/**
 * The marketing nav. Hrefs are ABSOLUTE (`/#pricing`, not `#pricing`) so the
 * same list works from a legal page or an SEO page, where a bare fragment would
 * scroll to nothing. On the landing page itself the browser treats `/#pricing`
 * as a same-document fragment and scrolls without a reload, so nothing is lost.
 */
export const PUBLIC_LINKS = [
  { label: 'Your expert', href: '/#expert' },
  { label: 'What it does', href: '/#capabilities' },
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
];

/** Auth pages: no marketing nav, so the narrow layout stays readable at 390px. */
export const NO_LINKS = [];

export const SIGN_IN = { label: 'Sign in', href: '/login' };
export const START_FREE = { label: 'Start free', href: '/Register' };
export const HOME = { label: 'Home', href: '/' };

// A router path (no fragment, not external) gets client-side navigation; a
// fragment or an external URL has to be a real browser navigation. Both render
// a genuine <a href>, which is what a crawler follows.
const isRoutePath = (href) => href.startsWith('/') && !href.includes('#');

function NavLink({ href, children, ...rest }) {
  return isRoutePath(href) ? (
    <Link to={href} {...rest}>{children}</Link>
  ) : (
    <a href={href} {...rest}>{children}</a>
  );
}

export default function PublicHeader({
  variant = 'solid',
  links = PUBLIC_LINKS,
  secondary = SIGN_IN,
  cta = START_FREE,
  // INIKRU_UI rule 1 — one crimson statement per view. On a marketing page the
  // header CTA IS that statement. On a sign-in page it is not: the form's own
  // submit button is, and two crimson buttons on one screen argue about which
  // one the visitor came for.
  ctaTone = 'primary',
}) {
  const transparent = variant === 'transparent';
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!transparent) return undefined;
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [transparent]);

  // Never leave the panel hanging open across a navigation.
  useEffect(() => { setOpen(false); }, [pathname]);

  // The scroll rule itself is untouched (>16px). `open` is added only so the
  // bar behind an open mobile panel is not a transparent strip floating over
  // the hero while the panel below it is opaque.
  const opaque = transparent ? scrolled || open : true;
  const hasMenu = links.length > 0;

  const ctaLook = ctaTone === 'quiet'
    ? 'border-[1.5px] border-gray-300 bg-white text-ink hover:border-ink'
    : 'bg-primary text-white hover:bg-primary-dark';

  const linkCls = `text-[14.5px] font-bold text-[#5A544E] transition-colors hover:text-ink rounded-[6px] ${FOCUS}`;
  // Hidden behind the hamburger only when there IS a hamburger. With no
  // marketing nav there is no menu to hide them in, so they stay inline —
  // "Home" + one CTA fits a 390px bar beside the wordmark.
  const revealCls = hasMenu ? 'hidden md:inline-flex' : 'inline-flex';

  return (
    <header
      className={`${transparent ? 'fixed inset-x-0' : 'sticky'} top-0 z-50 transition-all duration-300`}
      style={{
        background: opaque ? 'rgba(250,250,248,0.9)' : 'transparent',
        backdropFilter: opaque ? 'blur(12px)' : 'none',
        WebkitBackdropFilter: opaque ? 'blur(12px)' : 'none',
        borderBottom: `1px solid ${opaque ? 'rgba(30,20,10,0.08)' : 'transparent'}`,
      }}
    >
      <div className="mx-auto flex h-[66px] max-w-7xl items-center justify-between gap-4 px-5 lg:px-10">
        <Link to="/" aria-label="Inikru home" className={`inline-flex shrink-0 items-center rounded-[6px] ${FOCUS}`}>
          <img src={inikruLogo} alt="Inikru" className="h-6 w-auto" />
        </Link>

        {hasMenu && (
          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {links.map((l) => (
              <NavLink key={l.label} href={l.href} className={linkCls}>{l.label}</NavLink>
            ))}
          </nav>
        )}

        <div className="flex shrink-0 items-center gap-3 md:gap-4">
          {secondary && (
            <NavLink href={secondary.href} className={`${revealCls} items-center ${linkCls}`}>
              {secondary.label}
            </NavLink>
          )}
          {cta && (
            <NavLink
              href={cta.href}
              className={`${revealCls} group items-center justify-center gap-2 rounded-[10px] px-4 py-2.5 font-display text-[14px] font-bold transition-all motion-safe:hover:-translate-y-px md:px-5 ${ctaLook} ${FOCUS}`}
            >
              {cta.label}
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" />
            </NavLink>
          )}
          {hasMenu && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              className={`-mr-1.5 rounded-[6px] p-2.5 text-ink md:hidden ${FOCUS}`}
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          )}
        </div>
      </div>

      {hasMenu && open && (
        <div className="border-b border-line bg-bone px-5 pb-5 md:hidden">
          {links.map((l) => (
            <NavLink
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className={`block rounded-[6px] py-3 text-[16px] font-bold text-ink ${FOCUS}`}
            >
              {l.label}
            </NavLink>
          ))}
          {secondary && (
            <NavLink
              href={secondary.href}
              onClick={() => setOpen(false)}
              className={`block rounded-[6px] py-3 text-[16px] font-bold text-ink ${FOCUS}`}
            >
              {secondary.label}
            </NavLink>
          )}
          {cta && (
            <NavLink
              href={cta.href}
              onClick={() => setOpen(false)}
              className={`mt-2 flex w-full items-center justify-center gap-2 rounded-[10px] px-5 py-3 font-display text-[15px] font-bold ${ctaLook} ${FOCUS}`}
            >
              {cta.label}
              <ArrowRight size={17} />
            </NavLink>
          )}
        </div>
      )}
    </header>
  );
}
